import { Search, ShoppingCart, Star, ChevronRight, Tag, Heart, Plus } from 'lucide-react';
import { cn } from '../../../utils/helpers';

const products = [
  { id: 1, name: 'Premium Wireless Headphones', price: '$299.00', rating: 4.8, reviews: 124, image: 'https://picsum.photos/seed/audio/400/400', category: 'Electronics' },
  { id: 2, name: 'Minimalist Leather Wallet', price: '$45.00', rating: 4.9, reviews: 86, image: 'https://picsum.photos/seed/wallet/400/400', category: 'Accessories' },
  { id: 3, name: 'Ergonomic Office Chair', price: '$549.00', rating: 4.7, reviews: 210, image: 'https://picsum.photos/seed/chair/400/400', category: 'Furniture' },
  { id: 4, name: 'Smart Watch Series 5', price: '$399.00', rating: 4.6, reviews: 342, image: 'https://picsum.photos/seed/watch/400/400', category: 'Electronics' },
  { id: 5, name: 'Canvas Travel Backpack', price: '$89.00', rating: 4.8, reviews: 156, image: 'https://picsum.photos/seed/bag/400/400', category: 'Travel' },
  { id: 6, name: 'Mechanical Keyboard', price: '$159.00', rating: 4.9, reviews: 92, image: 'https://picsum.photos/seed/keyboard/400/400', category: 'Electronics' },
];

export default function Commerce() {
  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Marketplace</h1>
          <p className="text-neutral-500 dark:text-neutral-400">Discover products from the Messego community.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500" />
            <input 
              type="text" 
              placeholder="Search products..." 
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900/5 transition-all"
            />
          </div>
          <button className="p-2 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors relative">
            <ShoppingCart className="w-5 h-5 text-neutral-600 dark:text-neutral-300" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-neutral-900 text-white text-[10px] font-bold rounded-full flex items-center justify-center">3</span>
          </button>
        </div>
      </header>

      {/* Categories */}
      <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
        {['All', 'Electronics', 'Accessories', 'Furniture', 'Travel', 'Fashion', 'Home'].map((cat) => (
          <button 
            key={cat} 
            className={cn(
              "px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all",
              cat === 'All' ? "bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900" : "bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-300 hover:border-neutral-900 dark:hover:border-neutral-100"
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Featured Banner */}
      <div className="bg-neutral-900 rounded-[2rem] p-8 md:p-12 text-white overflow-hidden relative min-h-[300px] flex items-center">
        <div className="relative z-10 max-w-md space-y-4">
          <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
            <Tag className="w-3 h-3" />
            Limited Offer
          </div>
          <h2 className="text-4xl md:text-5xl font-bold leading-tight">Upgrade Your Workspace</h2>
          <p className="text-neutral-400 dark:text-neutral-500 text-sm md:text-base">Get up to 40% off on all office furniture and tech accessories this week.</p>
          <button className="bg-white text-neutral-900 px-6 py-3 rounded-xl font-bold hover:bg-neutral-100 transition-colors flex items-center gap-2">
            Shop Now
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden md:block">
          <img 
            src="https://picsum.photos/seed/workspace/800/600" 
            alt="Featured" 
            className="w-full h-full object-cover opacity-50 mix-blend-overlay"
            referrerPolicy="no-referrer"
          />
        </div>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {products.map((product) => (
          <div key={product.id} className="group bg-white dark:bg-neutral-900 rounded-3xl border border-neutral-200 dark:border-neutral-800 overflow-hidden hover:shadow-xl hover:shadow-neutral-200/50 transition-all duration-300">
            <div className="aspect-square overflow-hidden relative">
              <img 
                src={product.image} 
                alt={product.name} 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                referrerPolicy="no-referrer"
              />
              <button className="absolute top-4 right-4 p-2 rounded-full bg-white/80 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity">
                <Heart className="w-4 h-4 text-neutral-900 dark:text-neutral-50" />
              </button>
            </div>
            <div className="p-5 space-y-2">
              <div className="flex justify-between items-start">
                <p className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">{product.category}</p>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span className="text-xs font-bold">{product.rating}</span>
                </div>
              </div>
              <h3 className="font-bold text-neutral-900 dark:text-neutral-50 line-clamp-1">{product.name}</h3>
              <div className="flex items-center justify-between pt-2">
                <span className="text-lg font-bold">{product.price}</span>
                <button className="p-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-900 hover:text-white transition-colors">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
